import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Mail, Send } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { CelebrationInvitationManagement } from '@/pages/CelebrationInvitationManagement';
import { CelebrationEmailTemplatesPage } from '@/pages/CelebrationEmailTemplatesPage';

type WorkspaceTab = 'invitations' | 'templates';

export function InvitationWorkspacePage() {
  const params = useParams<{ id?: string; celebrationId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const tab: WorkspaceTab = requestedTab === 'templates' ? 'templates' : 'invitations';

  useEffect(() => {
    if (requestedTab !== null && requestedTab !== 'invitations' && requestedTab !== 'templates') {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', 'invitations');
      setSearchParams(nextParams, { replace: true });
    }
  }, [requestedTab, searchParams, setSearchParams]);

  function selectTab(nextTab: WorkspaceTab) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', nextTab);
    setSearchParams(nextParams);
  }

  return <PageContainer width="wide" className="space-y-5">
    <AppBreadcrumbs items={[{ label: 'Home', to: '/home' }, { label: 'Celebration', to: `/celebrations/${params.celebrationId || params.id}` }, { label: 'Invitations' }]} />
    <header className="rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-background p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-primary/10 p-3 text-primary"><Send className="h-6 w-6" /></div>
        <div><h1 className="text-2xl font-semibold">Invitations</h1><p className="mt-1 text-sm text-muted-foreground">Invite guests and manage reusable templates in one place.</p></div>
      </div>
    </header>
    <div role="tablist" aria-label="Invitation workspace" className="flex gap-2 border-b">
      <Button role="tab" aria-selected={tab === 'invitations'} variant={tab === 'invitations' ? 'secondary' : 'ghost'} onClick={() => selectTab('invitations')}>
        <Send className="mr-2 h-4 w-4" />Invitations
      </Button>
      <Button role="tab" aria-selected={tab === 'templates'} variant={tab === 'templates' ? 'secondary' : 'ghost'} onClick={() => selectTab('templates')}>
        <Mail className="mr-2 h-4 w-4" />Templates
      </Button>
    </div>
    <section role="tabpanel" aria-label={tab === 'templates' ? 'Templates' : 'Invitations'}>
      {tab === 'templates' ? <CelebrationEmailTemplatesPage embedded /> : <CelebrationInvitationManagement embedded />}
    </section>
  </PageContainer>;
}

export default InvitationWorkspacePage;